import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { IconChevronDown, IconSearch } from '@tabler/icons-react'

import Button from '@components/Button'

import { toast } from '@src/helpers/toaster'
import { useInviteUser } from '@queries/Teams'
import { useGetRoles } from '@queries/Roles'
import { requestErrorsHandler } from '@helpers/utils'
import { useAnchoredPosition } from '@helpers/hooks/useAnchoredPosition'
import { useHasPermission } from '@helpers/hooks/useHasPermission'
import { Perm } from '@type/permissions'

import type { OrgStructureRecord } from '@pages/Team/Members/types'

const POPOVER_WIDTH = 320

export interface NotInvitedCellProps {
  record: OrgStructureRecord
}

// "Not Invited" Access cell. Matches the chip-widget pattern: a
// chip-style trigger + chevron + click opens a popover with the same
// multi-role checkbox UX used everywhere else in this column.
//
// Perm-gated on TEAM_INVITE_MEMBER: a user without the codename sees
// the plain status text but no clickable trigger — clicking would
// only open a popover whose Invite button hits a 403. Hiding the
// trigger keeps the column visually consistent (the status badge
// still shows "Not invited") and avoids the dead-click trap.
export const NotInvitedCell: React.FC<NotInvitedCellProps> = ({ record }) => {
  const [pickerOpen, setPickerOpen] = useState(false)
  const canInvite = useHasPermission(Perm.TEAM_INVITE_MEMBER)
  // Trigger ref drives the portaled popover's viewport-relative
  // position (escapes the table's overflow clip).
  const triggerRef = useRef<HTMLButtonElement>(null)

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setPickerOpen((o) => !o)
  }, [])

  if (!canInvite) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-dashed border-gray-200 text-gray-400"
        data-qa="not-invited-cell-readonly"
      >
        Not invited
      </span>
    )
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50"
        data-qa="not-invited-cell"
      >
        Not invited
        <IconChevronDown size={12} className="text-gray-400" />
      </button>
      {pickerOpen && (
        <InvitePopover email={record.email} triggerRef={triggerRef} onClose={() => setPickerOpen(false)} />
      )}
    </>
  )
}

interface InvitePopoverProps {
  email: string
  triggerRef: React.RefObject<HTMLButtonElement | null>
  onClose: () => void
}

const InvitePopover: React.FC<InvitePopoverProps> = ({ email, triggerRef, onClose }) => {
  const { t } = useTranslation(['common', 'org'])
  const { data: allRoles } = useGetRoles()
  const { mutateAsync: invite, isLoading: isInviting } = useInviteUser()

  // Local pick state. Defaults to the "Member" system role so admins
  // don't have to make a positive choice for the common case.
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const [search, setSearch] = useState('')

  // Seed the default role exactly once (when roles load), via a ref guard
  // rather than depending on picked.size — otherwise clearing the last
  // selected role would immediately re-add Member, blocking intentional
  // deselection. Only the seeded SYSTEM Member role is auto-picked; never a
  // by-name match or the first role (could be Admin / a privileged custom role).
  const seededDefaultRef = useRef(false)
  useEffect(() => {
    if (seededDefaultRef.current) return
    if (!allRoles?.length) return
    seededDefaultRef.current = true
    const member = allRoles.find((r) => r.is_system && r.name === 'Member')
    if (member) setPicked(new Set([member.id]))
  }, [allRoles])

  // Portal escapes parent overflow. Outside-click checks BOTH the
  // trigger and the portaled popover since they live in different
  // parts of the DOM tree.
  const popoverRef = useRef<HTMLDivElement>(null)
  const position = useAnchoredPosition(triggerRef, {
    open: true,
    align: 'left',
    popoverWidth: POPOVER_WIDTH,
    // Lets the hook flip the popover above the trigger when it would not
    // fit below, and clamp it inside the viewport. Without this a row near
    // the bottom of the table opened a popover whose list and footer were
    // cut off, with no way to scroll to them.
    popoverRef,
  })

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      // Narrow with `instanceof Node` rather than an unchecked
      // `as Node` cast — Element.contains() requires a Node, and
      // non-Node targets (rare synthetic-event cases) close the
      // popover safely.
      if (!(e.target instanceof Node)) {
        onClose()
        return
      }
      const target = e.target
      const insideTrigger = triggerRef.current?.contains(target)
      const insidePopover = popoverRef.current?.contains(target)
      if (!insideTrigger && !insidePopover) {
        onClose()
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [onClose, triggerRef])

  const filtered = useMemo(() => {
    if (!allRoles) return []
    const q = search.trim().toLowerCase()
    if (!q) return allRoles
    return allRoles.filter((r) => r.name.toLowerCase().includes(q))
  }, [allRoles, search])

  const togglePick = useCallback((id: number) => {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleInvite = useCallback(async () => {
    if (picked.size === 0) return
    try {
      const result = await invite({
        invitees: [{ email, role_ids: [...picked] }],
      })
      if (result?.skipped_emails?.length) {
        toast.warning(t('common:invites.alreadyMember'))
      } else {
        toast.success(t('common:invites.inviteSuccess'))
      }
      onClose()
    } catch (err) {
      requestErrorsHandler(err)
    }
  }, [picked, invite, email, t, onClose])

  if (!position) return null

  return createPortal(
    <div
      ref={popoverRef}
      // maxHeight is set only when the popover cannot fit in full on
      // either side of the trigger. Applying it with a flex column keeps
      // the search box and the footer pinned while the role list takes the
      // remaining space and scrolls — rather than the popover running off
      // the viewport edge where it cannot be scrolled to at all.
      style={{ top: position.top, left: position.left, width: POPOVER_WIDTH, maxHeight: position.maxHeight }}
      // z-dropdown, not a raw z-50: this is a portaled menu and the raw
      // value sat BELOW page furniture it overlaps. On the last rows the
      // popover reaches into the pagination area, which then painted over
      // its lower half — the list and footer looked cut off when they were
      // actually just behind it.
      className="fixed z-dropdown flex flex-col rounded-md border border-gray-200 bg-white shadow-lg p-2"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-1 pt-1 pb-2 text-xs text-gray-500 truncate">
        {t('org:rolePicker.invitePopupTitle', { email })}
      </div>
      <div className="relative mb-2">
        <IconSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('org:rolePicker.searchPlaceholder') as string}
          aria-label={t('org:rolePicker.searchPlaceholder') as string}
          className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500"
        />
      </div>

      <div className="max-h-[220px] min-h-0 flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-3 text-xs text-gray-400 text-center">{t('org:rolePicker.noMatch') as string}</div>
        ) : (
          filtered.map((role) => {
            const checked = picked.has(role.id)
            return (
              <label
                key={role.id}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer rounded"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => togglePick(role.id)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="flex-1 text-sm text-gray-900 truncate">{role.name}</span>
                {role.is_system && (
                  <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">System</span>
                )}
              </label>
            )
          })
        )}
      </div>

      <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-500">
          {picked.size} role{picked.size === 1 ? '' : 's'} selected
        </span>
        <div className="flex gap-1.5">
          <Button color="white" onClick={onClose} disabled={isInviting} className="!px-3 !py-1 text-xs">
            {t('common:invites.cancelLabel')}
          </Button>
          <Button
            onClick={handleInvite}
            loading={isInviting}
            disabled={picked.size === 0}
            color="success"
            className="!px-3 !py-1 text-xs"
          >
            {t('common:invites.proceedLabel')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default React.memo(NotInvitedCell)
